import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import api from '../../utils/api';
import ThemeBackground from '../../components/Layout/ThemeBackground';
import { styled } from '@mui/material/styles';

const DatePickerWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  '& .react-datepicker-popper': {
    zIndex: 9999,
  },
  '& .datepicker-popper': {
    zIndex: 9999,
  },
  '& .react-datepicker': {
    fontFamily: theme.typography.fontFamily,
    zIndex: 9999,
  },
  '& .react-datepicker__header': {
    backgroundColor: theme.palette.grey[100],
  },
  '& .react-datepicker__day--selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '& .react-datepicker__day--keyboard-selected': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  '& .react-datepicker__day:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .react-datepicker-wrapper': {
    width: '100%',
    display: 'block'
  },
  '& .react-datepicker__input-container': {
    width: '100%',
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '8px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '4px',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main
    }
  }
}));

interface CounsellorData {
  isExistingEmployee?: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob: string;
  applicationExpiryDate: Date | null;
  completionDeadline?: Date | null;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dob?: string;
  applicationExpiryDate?: string;
}

function CounsellorCreationForm() {
  const navigate = useNavigate();
  const [isExistingEmployee, setIsExistingEmployee] = useState(false);
  const [counsellorData, setCounsellorData] = useState<CounsellorData>({
    isExistingEmployee: false,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: '',
    applicationExpiryDate: null,
    completionDeadline: null,
  });
  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [calculatedAge, setCalculatedAge] = useState<string>('');
  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | undefined>(undefined);
  const [, setIsCheckingEmail] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [tempDate, setTempDate] = useState<Date | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Predefined date arrays
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];
  const years = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  );

  const calculateAge = useCallback(() => {
    if (!day || !month || !year) return null;
    const birthDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    if (today.getMonth() < birthDate.getMonth() || 
        (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }, [day, month, year]);

  useEffect(() => {
    const age = calculateAge();
    setCalculatedAge(age ? age.toString() : '');
  }, [calculateAge]);

  // Check if email exists
  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      setIsCheckingEmail(true);
      // Check counsellors
      const counsellorResponse = await api.get(`/api/counsellors/check-email/${email}`);
      if (counsellorResponse.data.exists) {
        setApiError('Unable to create counsellor as this email address is already registered to an active counsellor');
        return true;
      }

      // Check users
      const userResponse = await api.get(`/api/users/check-email/${email}`);
      if (userResponse.data.exists) {
        setApiError('Unable to create counsellor as this email address is already registered to a user account');
        return true;
      }

      setApiError(undefined);
      return false;
    } catch (error: any) {
      console.error('Error checking email:', error);
      if (error.response?.data?.error) {
        setApiError(error.response.data.error);
      }
      // Don't block form submission on email check error
      return false;
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const validateForm = async (): Promise<boolean> => {
    const newErrors: Errors = {};

    // Always required fields
    if (!counsellorData.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!counsellorData.lastName?.trim()) newErrors.lastName = 'Last name is required';
    
    if (!counsellorData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(counsellorData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else {
      // Check if email exists
      const emailExists = await checkEmailExists(counsellorData.email);
      if (emailExists && apiError) {
        newErrors.email = apiError;
      }
    }

    // Phone validation - required for both flows
    if (!counsellorData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      // Remove spaces and common formatting characters for validation
      const cleanPhone = counsellorData.phone.replace(/[\s\-()]/g, '');
      
      // UK phone number validation (supports various formats)
      // Accepts: +44, 0044, or 0 prefix, followed by 9-10 digits
      const ukPhoneRegex = /^(?:(?:\+44|0044|0)\s?(?:\d\s?){9,10})$/;
      const cleanPhoneRegex = /^(?:(?:\+44|0044|0)\d{9,10})$/;
      
      if (!cleanPhoneRegex.test(cleanPhone) && !ukPhoneRegex.test(counsellorData.phone)) {
        newErrors.phone = 'Please enter a valid UK phone number (e.g., 07123456789 or +44 7123 456789)';
      } else if (cleanPhone.length < 10 || cleanPhone.length > 13) {
        newErrors.phone = 'Phone number must be 10-11 digits (excluding country code)';
      }
    }
    
    // Date validation for DOB
    if (!day || !month || !year) {
      newErrors.dob = 'Date of birth is required';
    } else {
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (isNaN(date.getTime())) {
        newErrors.dob = 'Please enter a valid date';
      }
    }

    // Deadline validation - different field names for different flows
    if (isExistingEmployee) {
      if (!counsellorData.completionDeadline) {
        newErrors.applicationExpiryDate = 'Completion deadline is required';
      } else {
        const now = new Date();
        if (counsellorData.completionDeadline <= now) {
          newErrors.applicationExpiryDate = 'Completion deadline must be in the future';
        }
      }
    } else {
      if (!counsellorData.applicationExpiryDate) {
        newErrors.applicationExpiryDate = 'Application expiry date is required';
      } else {
        const now = new Date();
        if (counsellorData.applicationExpiryDate <= now) {
          newErrors.applicationExpiryDate = 'Application expiry date must be in the future';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError(undefined);

    try {
      const isValid = await validateForm();
      if (!isValid) {
        setIsSubmitting(false);
        return;
      }

      if (isExistingEmployee) {
        // Existing employee flow - admin provides basic info, counsellor completes the rest
        const formattedDob = `${year}-${month}-${day}`;
        const counsellorPayload = {
          isExistingEmployee: true,
          firstName: counsellorData.firstName,
          lastName: counsellorData.lastName,
          email: counsellorData.email,
          phone: counsellorData.phone,
          dob: formattedDob,
          completionDeadline: counsellorData.completionDeadline?.toISOString()
        };

        await api.post('/api/counsellors/add-existing', counsellorPayload);
        setSnackbar({
          open: true,
          message: 'Counsellor record created. An email has been sent for them to complete their profile.',
          severity: 'success'
        });
      } else {
        // New applicant flow - full data, send application form
        const formattedDob = `${year}-${month}-${day}`;
        const counsellorPayload = {
          ...counsellorData,
          dob: formattedDob,
          age: parseInt(calculatedAge)
        };

        await api.post('/api/counsellors', counsellorPayload);
        setSnackbar({
          open: true,
          message: 'Counsellor added successfully and application form has been sent!',
          severity: 'success'
        });
      }
        // Add a small delay before redirecting
        setTimeout(() => {
          navigate('/');
        }, 500);
    } catch (error: any) {
      if (error.response?.data?.error) {
        setApiError(error.response.data.error);
      } else {
        setApiError('An error occurred while creating the counsellor. Please try again.');
      }
      console.error('Error creating counsellor:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-6">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center">
                <div className="flex space-x-4">
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                      backgroundColor: '#2196f3',
                      '&:hover': {
                        backgroundColor: '#1976d2'
                      }
                    }}
                    onClick={() => navigate('/')}
                  >
                    Back to Home
                  </Button>
                </div>

                <div className="text-right">
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    className="theme-text-primary"
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 800,
                      fontSize: '2.5rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Counsellor Creation
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="max-w-7xl mx-auto py-8">
            {apiError && (
              <div className="error-message mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                {apiError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Existing Employee Toggle */}
              <div className="glass-card-light p-4 mb-6 border-l-4 border-blue-500">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="existingEmployee"
                    checked={isExistingEmployee}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsExistingEmployee(checked);
                      setCounsellorData({ ...counsellorData, isExistingEmployee: checked });
                    }}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="existingEmployee" className="ml-3 block">
                    <span className="text-base font-semibold theme-text-primary">Existing Employee</span>
                    <p className="text-sm theme-text-secondary mt-1">
                      Enable this for existing employees. You'll provide their basic information (name, email, phone, DOB), and they'll receive an email to complete the rest of their profile (address, qualifications, documents, availability).
                    </p>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    className="theme-input w-full p-2 border rounded-lg"
                    value={counsellorData.firstName}
                    onChange={(e) => setCounsellorData({ ...counsellorData, firstName: e.target.value })}
                  />
                  {errors.firstName && <p className="text-sm text-red-400 mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    className="theme-input w-full p-2 border rounded-lg"
                    value={counsellorData.lastName}
                    onChange={(e) => setCounsellorData({ ...counsellorData, lastName: e.target.value })}
                  />
                  {errors.lastName && <p className="text-sm text-red-400 mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    className={`theme-input w-full p-2 border rounded-lg ${
                      errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                    value={counsellorData.email}
                    onChange={(e) => {
                      setCounsellorData({ ...counsellorData, email: e.target.value });
                      // Clear error when user starts typing
                      if (errors.email) {
                        setErrors({ ...errors, email: undefined });
                      }
                    }}
                    placeholder="user@example.com"
                  />
                  {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    className={`theme-input w-full p-2 border rounded-lg ${
                      errors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                    value={counsellorData.phone}
                    onChange={(e) => {
                      setCounsellorData({ ...counsellorData, phone: e.target.value });
                      // Clear error when user starts typing
                      if (errors.phone) {
                        setErrors({ ...errors, phone: undefined });
                      }
                    }}
                    placeholder="07123456789 or +44 7123 456789"
                  />
                  {errors.phone && <p className="text-sm text-red-500 mt-1">{errors.phone}</p>}
                  {!errors.phone && counsellorData.phone && (
                    <p className="text-xs text-gray-500 mt-1">UK format: 07XXX XXXXXX or +44 7XXX XXXXXX</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">Date of Birth</label>
                <div className="flex gap-2">
                  <select 
                    value={day} 
                    onChange={(e) => setDay(e.target.value)} 
                    className="theme-input p-2 border rounded-lg flex-1"
                    data-field="dob"
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                    ))}
                  </select>
                  <select 
                    value={month} 
                    onChange={(e) => setMonth(e.target.value)} 
                    className="theme-input p-2 border rounded-lg flex-1"
                    data-field="dob"
                  >
                    <option value="">Month</option>
                    {months.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <select 
                    value={year} 
                    onChange={(e) => setYear(e.target.value)} 
                    className="theme-input p-2 border rounded-lg flex-1"
                    data-field="dob"
                  >
                    <option value="">Year</option>
                    {years.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                {calculatedAge && <p className="mt-2 theme-text-secondary">Age: {calculatedAge} years</p>}
                {errors.dob && <p className="text-sm text-red-400 mt-1">{errors.dob}</p>}
              </div>

              {/* Application Expiry Date for new applicants, Completion Deadline for existing employees */}
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-2">
                  {isExistingEmployee ? 'Completion Deadline' : 'Application Expiry Date'}
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const currentDate = isExistingEmployee ? counsellorData.completionDeadline : counsellorData.applicationExpiryDate;
                    setTempDate(currentDate || new Date());
                    setDatePickerOpen(true);
                  }}
                  className="theme-input w-full p-2 border rounded-lg text-left"
                >
                  {(isExistingEmployee ? counsellorData.completionDeadline : counsellorData.applicationExpiryDate)
                    ? new Date(isExistingEmployee ? counsellorData.completionDeadline! : counsellorData.applicationExpiryDate!).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                    : (isExistingEmployee ? "Select completion deadline" : "Select expiry date")}
                </button>
                {errors.applicationExpiryDate && (
                  <p className="text-sm text-red-400 mt-1">{errors.applicationExpiryDate}</p>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    minWidth: '160px',
                    backgroundColor: '#2196f3',
                    '&:hover': {
                      backgroundColor: '#1976d2'
                    }
                  }}
                >
                  {isSubmitting ? 'Creating...' : 'Create Counsellor'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
      </div>

      {/* Date Picker Dialog */}
      <Dialog
        open={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600 }}>
            {isExistingEmployee ? 'Select Completion Deadline' : 'Select Application Expiry Date'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
          <DatePicker
            selected={tempDate}
            onChange={(date: Date | null) => setTempDate(date)}
            dateFormat="dd/MM/yyyy"
            minDate={new Date()}
            inline
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDatePickerOpen(false)}
            sx={{ 
              textTransform: 'none', 
              color: 'rgba(255, 255, 255, 0.6)',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.8)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (tempDate) {
                // Set time to end of day (23:59:59) for the selected date
                const endOfDay = new Date(tempDate);
                endOfDay.setHours(23, 59, 59, 999);
                
                if (isExistingEmployee) {
                  setCounsellorData({ ...counsellorData, completionDeadline: endOfDay });
                } else {
                  setCounsellorData({ ...counsellorData, applicationExpiryDate: endOfDay });
                }
              }
              setDatePickerOpen(false);
            }}
            variant="contained"
            sx={{ 
              textTransform: 'none',
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeBackground>
  );
};

export default CounsellorCreationForm;